import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiPaths } from 'modules/calendar/constants/apiPaths';
import { useEffect, useMemo } from 'react';
import { getKomboJobs } from 'services/integration/api';
import {
  KomboJob,
  KomboJobsResponse
} from 'services/integration/types/komboJob';
import { ToastType, useToast } from 'services/toast';

const PAGE_SIZE = 100;

export type KomboJobFilters = {
  nameContains?: string;
  remoteIds?: string;
  jobCodes?: string;
  statuses?: string;
};

/**
 * Server-side cursor pagination for the new `/fetch-kombo-jobs` endpoint.
 * Returns one page at a time — `currentPageIndex` advances forward via
 * `fetchNextPage`. Backwards navigation reuses already-loaded pages
 * (TanStack caches them in `data.pages`).
 */
export const useKomboJobsFetcher = ({
  organizationId,
  filters,
  isForAnalysis
}: {
  organizationId: string;
  filters: KomboJobFilters;
  isForAnalysis?: boolean;
}) => {
  const { showToast } = useToast();

  const query = useInfiniteQuery<KomboJobsResponse>({
    queryKey: [
      ApiPaths.INTEGRATION_KOMBO_JOBS,
      organizationId,
      filters.nameContains ?? '',
      filters.remoteIds ?? '',
      filters.jobCodes ?? '',
      filters.statuses ?? '',
      isForAnalysis ?? false
    ],
    queryFn: ({ pageParam }) =>
      getKomboJobs({
        pageSize: PAGE_SIZE,
        next: (pageParam as string | null) ?? null,
        isForAnalysis,
        ...filters
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    retry: 1,
    staleTime: 1000 * 60 * 30
  });

  // Surface the error as a toast once TanStack has exhausted its retry. We
  // intentionally don't swallow inside `queryFn` — doing so would defeat
  // `retry: 1` and corrupt the infinite-query `pages` array if a follow-up
  // page fetch failed (TanStack would treat the empty stub as the new
  // last page and lock `hasNextPage` to false).
  useEffect(() => {
    if (query.error) {
      showToast({
        message: 'An error occurred while fetching Kombo jobs.',
        type: ToastType.Error
      });
    }
  }, [query.error, showToast]);

  const pages: KomboJob[][] = useMemo(
    () => query.data?.pages.map((p) => p.data) ?? [],
    [query.data]
  );

  return {
    pages,
    isLoading: query.isLoading || query.isRefetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
    pageSize: PAGE_SIZE
  };
};
